import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Modal from "react-modal";
import PaymentLinkTable from "../../payment-link-system/PaymentLinkTable";
import "../../../styles/payrole-system/payrole-processor.css";
import { EyeIcon, ReloadIcon, ResolveIcon } from "../../../images/icons/icons";
import { discrepancyTabs, truncateText } from "../PayroleMocks";
import StaffDiscrepancy from "./discrepancy-management/StaffDiscrepancy";
import Loader from "../../ui/Loader";
import {
  formatDateTime,
  getMonthYearFromYYMM,
} from "../../common-data/Converts";
import PayrollDetailsModal from "../PayrollDetailsModal";

const DiscrepancyManagementTable = ({
  callFunction,
  isPage,
  setIsPage,
  pageLimit,
  setPageLimit,
  userInfoData,
  setFilters,
  filters,
}) => {
  const {
    staffPayroleDiscrepancyData,
    staffPayroleDiscrepancyLoading,
    staffPayroleDiscrepancyError,
  } = useSelector((state) => state.payrole);

  const isAdmin = userInfoData?.account_type === "admin";

  const [activeTab, setActiveTab] = useState("pending");
  const iniDatas = {
    pagination: [],
    allData: [],
  };
  const [allDatas, setAllDatas] = useState(iniDatas);
  const [statsData, setStatsData] = useState({});
  const iniModValues = {
    isModOpen: false,
    modName: "",
    rowData: "",
  };
  const [modValues, setModValues] = useState(iniModValues);
  const [expandedRemarks, setExpandedRemarks] = useState(new Set());

  const closeModal = () => {
    setModValues(iniModValues);
  };

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);

    if (setFilters) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        status: tabKey,
      }));
    }
  };

  const toggleRemarkExpansion = (rowId) => {
    setExpandedRemarks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    if (
      !staffPayroleDiscrepancyLoading &&
      !staffPayroleDiscrepancyError &&
      staffPayroleDiscrepancyData?.status &&
      staffPayroleDiscrepancyData?.stats
    ) {
      setStatsData(staffPayroleDiscrepancyData.stats);
    } else {
      setStatsData({});
    }
  }, [
    staffPayroleDiscrepancyLoading,
    staffPayroleDiscrepancyError,
    staffPayroleDiscrepancyData,
  ]);

  useEffect(() => {
    if (
      !staffPayroleDiscrepancyLoading &&
      !staffPayroleDiscrepancyError &&
      staffPayroleDiscrepancyData?.status &&
      staffPayroleDiscrepancyData?.data?.length > 0
    ) {
      setAllDatas((prevs) => ({
        ...prevs,
        allData: staffPayroleDiscrepancyData.data,
        pagination:
          staffPayroleDiscrepancyData?.pagination?.length > 0
            ? staffPayroleDiscrepancyData.pagination
            : [],
      }));
    } else {
      setAllDatas(iniDatas);
    }
  }, [
    staffPayroleDiscrepancyLoading,
    staffPayroleDiscrepancyError,
    staffPayroleDiscrepancyData,
  ]);

  const columns = [
    {
      dataField: "month_year",
      text: "Payrole Month",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {row?.month_year ? getMonthYearFromYYMM(row.month_year) : "-"}
          </span>
        );
      },
    },
    {
      dataField: "user_id",
      text: "Employee",
      formatter: (_, row) => (
        <div className="pp-discrepancy-employee-info">
          <p className="pp-discrepancy-employee-name">
            {row?.user_name || "NA"}
          </p>
          <p className="pp-discrepancy-employee-phone">
            {row?.user_mobile || ""}
          </p>
          <p className="pp-discrepancy-employee-phone">
            {row?.user_email || ""}
          </p>
        </div>
      ),
    },
    {
      dataField: "account_type",
      text: "Role",
      formatter: (_, row) => (
        <span className="tb-data">{row?.account_type?.replace(/_/g, " ")}</span>
      ),
    },
    {
      dataField: "gym_id",
      text: "Gym Name",
      formatter: (_, row) => (
        <span className="tb-data minWidth150">{row?.gym_name || "NA"}</span>
      ),
    },
    {
      dataField: "discrepancy_count",
      text: "Discrepancy Count",
      formatter: (_, row) => (
        <span className="tb-data">{row?.discrepancy_count || 0}</span>
      ),
    },
    {
      dataField: "date_added",
      text: "Date Added",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {row?.date_added ? formatDateTime(row.date_added) : "-"}
          </span>
        );
      },
    },
    ...(activeTab !== "pending"
      ? [
          {
            dataField: "description",
            text: "HR Notes",
            formatter: (_, row) => {
              const description = row?.description || "NA";
              const isExpanded = expandedRemarks.has(row.id || row.uid);
              const shouldTruncate = description.length > 50;

              return (
                <div className="pp-discrepancy-description-container">
                  <span className="pp-discrepancy-description">
                    {isExpanded || !shouldTruncate
                      ? description
                      : truncateText(description)}
                  </span>
                  {shouldTruncate && (
                    <button
                      className="pp-view-more-btn"
                      onClick={() => toggleRemarkExpansion(row.id || row.uid)}
                    >
                      {isExpanded ? "View Less" : "View More"}
                    </button>
                  )}
                </div>
              );
            },
          },
        ]
      : []),
    {
      dataField: "actions",
      text: "Actions",
      formatter: (_, row) => {
        const isPending = row.status === "pending";
        const isApproved = row.status === "approved";
        const isRejected = row.status === "rejected";

        return (
          <div className="pp-employee-payroll-actions">
            <button
              key="view-payroll"
              type="button"
              className="pp-employee-payroll-action-button pp-outlined-border"
              onClick={() =>
                setModValues((prevs) => ({
                  ...prevs,
                  isModOpen: true,
                  modName: "view-payroll",
                  rowData: row,
                }))
              }
              style={{ marginRight: "8px" }}
            >
              <EyeIcon />
              View
            </button>

            <button
              type="button"
              className={`${
                isAdmin && isPending
                  ? "pp-discrepancy-action-button"
                  : "pp-discrepancy-status-badge"
              } ${
                isApproved
                  ? "pp-status-active"
                  : isRejected
                  ? "pp-status-inactive"
                  : isAdmin && isPending
                  ? "pp-status-pending"
                  : ""
              }`}
              disabled={!isPending}
              onClick={() =>
                isAdmin &&
                setModValues((prevs) => ({
                  ...prevs,
                  isModOpen: true,
                  modName: "staff_discrepancy",
                  rowData: row,
                }))
              }
            >
              {isAdmin && isPending ? (
                <>
                  <ResolveIcon /> Resolve
                </>
              ) : (
                row?.status?.replace(/_/g, " ") || "NA"
              )}
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="pp-discrepancy-table-container">
      <div className="pp-bulk-header">
        <div className="pp-employee-payroll-card-header">
          <span> Discrepancy Management</span>
          <button variant="dark" type="button" onClick={callFunction}>
            <ReloadIcon />
          </button>
        </div>
      </div>

      <div className="pp-discrepancy-tabs">
        {discrepancyTabs.map((tab) => (
          <button
            key={tab.key}
            className={`pp-discrepancy-tab-item ${
              activeTab === tab.key ? "pp-discrepancy-tab-active" : ""
            }`}
            onClick={() => handleTabChange(tab.key)}
          >
            {tab.label} (
            {tab.key === "rejected"
              ? statsData?.rejected || 0
              : tab.key === "approved"
              ? statsData?.approved || 0
              : statsData?.pending || 0}
            )
          </button>
        ))}
      </div>

      <div className="pp-employee-payroll-table-wrapper">
        {staffPayroleDiscrepancyLoading ? (
          <Loader />
        ) : (
          <PaymentLinkTable
            tableData={allDatas.allData}
            tableColumns={columns}
            tablePagination={allDatas.pagination}
            isPage={isPage}
            setIsPage={(value) => setIsPage(value)}
            pageLimit={pageLimit}
            setPageLimit={setPageLimit}
          />
        )}
      </div>

      {modValues.isModOpen && modValues.modName === "view-payroll" && (
        <PayrollDetailsModal
          userInfoData={userInfoData}
          onClose={closeModal}
          rowData={modValues.rowData}
        />
      )}

      <Modal
        isOpen={modValues.isModOpen}
        onRequestClose={closeModal}
        contentLabel="Detail Modal"
        ariaHideApp={false}
      >
        <StaffDiscrepancy
          userInfoData={userInfoData}
          callFunction={callFunction}
          rowData={modValues.rowData}
          closeModalPrev={closeModal}
        />
      </Modal>
    </div>
  );
};

export default DiscrepancyManagementTable;